import { useState } from "react";
import { Check, ChevronDown, X } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { BrutalBadge } from "./BrutalBadge";

export interface BrutalMultiSelectOption {
  value: string;
  label: string;
  icon?: string;
}

interface BrutalMultiSelectProps {
  options: BrutalMultiSelectOption[];
  selected: string[];
  onChange: (selected: string[]) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  className?: string;
  disabled?: boolean;
}

export function BrutalMultiSelect({
  options,
  selected,
  onChange,
  placeholder = "Seleziona...",
  searchPlaceholder = "Cerca...",
  emptyMessage = "Nessun risultato trovato",
  className,
  disabled = false,
}: BrutalMultiSelectProps) {
  const [open, setOpen] = useState(false);

  const handleSelect = (value: string) => {
    const newSelected = selected.includes(value)
      ? selected.filter((v) => v !== value)
      : [...selected, value];
    onChange(newSelected);
  };

  const handleRemove = (value: string, e?: React.MouseEvent) => {
    e?.stopPropagation();
    onChange(selected.filter((v) => v !== value));
  };

  const selectedOptions = options.filter((opt) =>
    selected.includes(opt.value)
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          disabled={disabled}
          className={cn(
            "w-full border-[3px] border-dark bg-card p-3 text-left font-body text-base transition-all",
            "flex items-center justify-between gap-2",
            "hover:shadow-[4px_4px_0px_0px] hover:shadow-dark hover:translate-x-[-2px] hover:translate-y-[-2px]",
            "focus:outline-none focus:ring-4 focus:ring-primary",
            "disabled:cursor-not-allowed disabled:opacity-50",
            className
          )}
        >
          <div className="flex flex-1 flex-wrap gap-2">
            {selectedOptions.length > 0 ? (
              selectedOptions.map((option) => (
                <BrutalBadge
                  key={option.value}
                  variant="secondary"
                  className="flex items-center gap-1 text-xs"
                >
                  {option.icon && <span>{option.icon}</span>}
                  <span>{option.label}</span>
                  <button
                    type="button"
                    onClick={(e) => handleRemove(option.value, e)}
                    className="ml-1 hover:text-destructive"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </BrutalBadge>
              ))
            ) : (
              <span className="text-muted-foreground">{placeholder}</span>
            )}
          </div>
          <ChevronDown
            className={cn(
              "h-5 w-5 shrink-0 transition-transform",
              open && "rotate-180"
            )}
          />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] border-[3px] border-dark bg-card p-0 shadow-[6px_6px_0px_0px] shadow-dark"
        align="start"
      >
        <Command className="bg-transparent">
          <CommandInput
            placeholder={searchPlaceholder}
            className="border-b-[3px] border-dark"
          />
          <CommandList className="max-h-[300px]">
            <CommandEmpty className="py-6 text-center text-sm">
              {emptyMessage}
            </CommandEmpty>
            <CommandGroup>
              {options.map((option) => {
                const isSelected = selected.includes(option.value);
                return (
                  <CommandItem
                    key={option.value}
                    onSelect={() => handleSelect(option.value)}
                    className={cn(
                      "flex cursor-pointer items-center gap-2 px-3 py-2",
                      "data-[selected=true]:bg-primary data-[selected=true]:text-dark"
                    )}
                  >
                    <div
                      className={cn(
                        "flex h-5 w-5 items-center justify-center border-[2px] border-dark bg-card transition-all",
                        isSelected && "bg-dark"
                      )}
                    >
                      {isSelected && <Check className="h-3 w-3 text-card" />}
                    </div>
                    {option.icon && (
                      <span className="text-base">{option.icon}</span>
                    )}
                    <span className="flex-1">{option.label}</span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
